interface ConfirmationModalProps {
  open: boolean;
  title?: string;
  subtitle?: string;
  confirmText?: string;
  cancelText?: string;
  onConfirm: () => void;
  onCancel: () => void;
}

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  open,
  title = "Log out of your account?",
  subtitle = "You'll be signed out and will need to log in again to continue.",
  confirmText = "Log out",
  onConfirm,
  onCancel,
}) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30">
      <motion.div
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.8, opacity: 0 }}
        transition={{ duration: 0.25 }}
        className="bg-white rounded-xl shadow-2xl w-full max-w-md flex flex-col items-center px-8 pt-8 pb-6"
      >
        {logoutIcon}
        <div className="font-semibold text-xl text-gray-900 mb-2 text-center">{title}</div>
        <div className="text-center text-gray-500 mb-4 text-base font-normal">{subtitle}</div>
        <div className="w-full border-t border-gray-200 mb-4" />
        <div className="flex gap-3 w-full justify-center px-2">
          <Button variant="outline" size="small" onClick={onCancel}>
            Cancel
          </Button>
          <CustomButton variant="destructive" className="bg-[#F44336] text-white" onClick={onConfirm}>
            {confirmText}
          </CustomButton>
        </div>
      </motion.div>
    </div>
  );
};
import React, { useState } from "react";
import { motion } from "framer-motion";
import { useSelector } from "react-redux";
import { loginSelector } from "@/store/features/loginSlice";
import Icon from "@/common/ui/Image";
import { User } from "@/types/user.types";
import LogoutModal from "./LogoutModal";
import ProfileIcon from "./ProfileIcon";
import { useNavigationGuard } from "@/utils/helper/navigation";
import { Title } from "@/common/ui/Text";
import { companyAccountType } from "@/utils/constant/login.constant";
import { Button as CustomButton } from "@/components/ui/button";
import ButtonModal from "../../common/modal/ButtonModal";
import DashboardSearch from "../search/DashboardSearch";
import { logoutIcon } from "@/dmr/common/SvgIcons";
import Button from "@/common/ui/Button";
import Notification from "@/components/common/Notification";
// import Notification from "../../common/Notification";

interface ProfileModalProps {
  currentUser: User;
  onClose: (props?: any) => void;
}

const ProfileModal: React.FC<ProfileModalProps> = ({ currentUser, onClose }) => {
  const [isLogoutModalOpen, setIsOpenLogoutModalOpen] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const handleNavigation = useNavigationGuard();

  const handleLogout = () => {
    setShowConfirmModal(true);
  };

  const handleConfirmLogout = () => {
    setShowConfirmModal(false);
    setIsOpenLogoutModalOpen(true);
  };

  const handleCancelLogout = () => {
    setShowConfirmModal(false);
  };

  const onClickLink = (link: string) => {
    handleNavigation(link);
    onClose();
  };

  return (
    <>
      {isLogoutModalOpen ? (
        <LogoutModal onClose={() => setIsOpenLogoutModalOpen(false)} userProfile={{ name: currentUser.name, profilePic: currentUser?.profilePic }} />
      ) : (
        <div className="flex flex-col">
          <div className="flex items-center space-x-2 bg-primaryOpacity-10 p-4 pr-11 rounded-md">
            {currentUser?.profilePic ? (
              <img src={currentUser.profilePic} alt="Profile" className="size-8 rounded-full object-cover" />
            ) : (
              <ProfileIcon name={currentUser.name} hideName className=" bg-primaryOpacity-10 w-8 h-8" />
            )}
            <div>
              <div className="font-bold text-13 !leading-normal text-text-default truncated-text hover:line-clamp-4">{currentUser.name}</div>
              {currentUser.userType !== "IDV" && (
                <div className="text-text-default font-normal  text-xs !leading-normal capitalize">{currentUser.role?.toLocaleLowerCase()}</div>
              )}
            </div>
          </div>
          <div className="p-4 flex flex-col gap-1">
            <CustomButton variant="ghost" className="justify-start !px-0 gap-2" onClick={() => onClickLink("/home/profile")}>
              <Icon src="/icons/profile.svg" className="size-6 pl-1" alt="profile" />
              <span className="font-normal !leading-normal text-13 text-text-default"> My profile</span>
            </CustomButton>

            <CustomButton variant="ghost" className="text-13 text-text-error justify-start !px-0 gap-2" onClick={handleLogout}>
              <Icon src="/icons/logout.svg" className="size-6" alt="profile" />
              <span className="font-normal !leading-normal text-13 "> Logout</span>
            </CustomButton>
          </div>
        </div>
      )}
      <ConfirmationModal
        open={showConfirmModal}
        title="Log out of your account?"
        subtitle="You'll be signed out and will need to log in again to continue."
        confirmText="Log out"
        cancelText="Cancel"
        onConfirm={handleConfirmLogout}
        onCancel={handleCancelLogout}
      />
    </>
  );
};

const DashboardHeader: React.FC = () => {
  const { currentUser, currentOrg } = useSelector(loginSelector);
  const isCompanyAccount = currentUser && companyAccountType.includes(currentUser.userType);

  return (
    <motion.header className="bg-white shadow md:shadow-sm px-4 py-2 max-h-12 flex justify-between items-center fixed md:static w-full md:w-auto top-0 z-40">
      <DashboardSearch />
      <motion.div className=" flex items-center gap-x-2 md:gap-x-4">
        {/* Organization Name if any */}
        {isCompanyAccount && currentOrg?.SignupOrg?.org_name && (
          <div className="rounded py-1 px-2 bg-primaryOpacity-5 flex items-center gap-2">
            {currentOrg?.SignupOrg?.log_path ? (
              <Icon src={currentOrg?.SignupOrg?.log_path} alt="" className=" h-5 w-5" />
            ) : (
              <ProfileIcon name={currentOrg?.SignupOrg?.org_name} hideName className=" bg-primaryOpacity-10 w-5 h-5 !border-[0.5px]" />
            )}
            <Title text={currentOrg?.SignupOrg?.org_name} className="hidden md:block !text-text-primary truncate max-w-350" />
            {/* <Icon src="/icons/action-icon.svg" alt="" className=" h-5 w-5" /> */}
          </div>
        )}
        {/* Notification */}
        <Notification />
        {/* Profile */}
        <ButtonModal modalContent={currentUser ? <ProfileModal currentUser={currentUser} onClose={() => {}} /> : null}>
          {currentUser?.profilePic ? (
            <img src={currentUser.profilePic} alt="Profile" className="size-8 rounded-full object-cover" />
          ) : (
            currentUser?.name && <ProfileIcon name={currentUser?.name} hideName className=" bg-primaryOpacity-10 w-8 h-8" />
          )}
        </ButtonModal>
      </motion.div>
    </motion.header>
  );
};

export default DashboardHeader;
